import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  ArrowRight, Bike, ChefHat, CircleCheck, Columns3, List, MessageCircle, Plus, Receipt, RefreshCw, Search, TriangleAlert, Wallet, X,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { EmptyState } from '@/components/EmptyState';
import { Pagination } from '@/components/Pagination';
import { useCompanyCurrency, useLabels } from '@/contexts/WorkspaceContext';
import {
  nextStatus, Order, OrderBoard, OrderListParams, OrderPaymentStatus, OrderStatus, OrderType, ordersService, ORDER_TYPE_LABEL,
} from '@/services/orders.service';
import { Chip, formatMoney } from '@/pages/catalog/shared';
import OrderDrawer from './OrderDrawer';
import OrderNotifyModal from './OrderNotifyModal';
import { useAuth } from '@/hooks/useAuth';
import {
  ElapsedPill, itemsSummary, NEXT_LABEL, PaymentBadge, STATUS_TONE, StatusBadge, timeOf, TypeBadge, TYPE_ICON, useNow, dateTimeOf,
} from './orderShared';

type View = 'board' | 'list';
type ColumnKey = 'new' | 'preparing' | 'ready' | 'out_for_delivery' | 'completed';
const VIEW_KEY = 'slightor_orders_view';
const POLL_MS = 15_000;
const LIST_LIMIT = 50;

const COLUMNS: Array<{ key: ColumnKey; label: string; hint: string }> = [
  { key: 'new', label: 'New', hint: 'Waiting for the kitchen' },
  { key: 'preparing', label: 'Preparing', hint: 'In the kitchen' },
  { key: 'ready', label: 'Ready', hint: 'To serve or collect' },
  { key: 'out_for_delivery', label: 'Out for delivery', hint: 'With the rider' },
  { key: 'completed', label: 'Completed today', hint: 'Done and dusted' },
];

type StatusFilter = 'all' | 'open' | 'completed' | 'cancelled';
const STATUS_FILTER: Record<StatusFilter, string | undefined> = {
  all: undefined, open: 'new,preparing,ready,out_for_delivery', completed: 'completed', cancelled: 'cancelled',
};
type RangeFilter = 'today' | 'yesterday' | '7d' | '30d' | 'all';
const RANGE_LABEL: Record<RangeFilter, string> = { today: 'Today', yesterday: 'Yesterday', '7d': 'Last 7 days', '30d': 'Last 30 days', all: 'All time' };

/** `dayStart` = the company's midnight (from the board) so "Today" matches the board, not the browser's zone. */
function rangeParams(r: RangeFilter, dayStart?: string): { from?: string; to?: string } {
  const start = dayStart ? new Date(dayStart) : new Date(new Date().setHours(0, 0, 0, 0));
  const day = 864e5;
  switch (r) {
    case 'today': return { from: start.toISOString() };
    case 'yesterday': return { from: new Date(start.getTime() - day).toISOString(), to: start.toISOString() };
    case '7d': return { from: new Date(start.getTime() - 6 * day).toISOString() };
    case '30d': return { from: new Date(start.getTime() - 29 * day).toISOString() };
    default: return {};
  }
}

export default function OrdersPage() {
  const t = useLabels();
  const navigate = useNavigate();
  const { orderUid } = useParams<{ orderUid?: string }>();
  const isAdmin = useAuth().user?.role === 'company_admin';
  const [notifyOpen, setNotifyOpen] = useState(false);
  const closeNotify = useCallback(() => setNotifyOpen(false), []);
  const companyCurrency = useCompanyCurrency();
  const now = useNow();
  const plural = t('orders');
  const noun = t('orders', 'singular');

  const [view, setView] = useState<View>(() => {
    try { return (localStorage.getItem(VIEW_KEY) as View) || 'board'; } catch { return 'board'; }
  });
  useEffect(() => { try { localStorage.setItem(VIEW_KEY, view); } catch { /* storage blocked */ } }, [view]);

  // ── board ──
  const [board, setBoard] = useState<OrderBoard | null>(null);
  const [boardError, setBoardError] = useState(false);
  const [advancing, setAdvancing] = useState<string | null>(null);
  const boardReq = useRef(0);

  const loadBoard = useCallback(async (quiet = false) => {
    const id = ++boardReq.current;
    if (!quiet) setBoardError(false);
    try {
      const b = await ordersService.board();
      if (id === boardReq.current) { setBoard(b); setBoardError(false); }
    } catch {
      if (id === boardReq.current && !quiet) setBoardError(true);
    }
  }, []);

  useEffect(() => {
    void loadBoard();
    const h = setInterval(() => { if (document.visibilityState === 'visible') void loadBoard(true); }, POLL_MS);
    return () => clearInterval(h);
  }, [loadBoard]);

  // ── list ──
  const [rows, setRows] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState(false);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [statusF, setStatusF] = useState<StatusFilter>('all');
  const [typeF, setTypeF] = useState<OrderType | 'all'>('all');
  const [payF, setPayF] = useState<OrderPaymentStatus | 'all'>('all');
  const [range, setRange] = useState<RangeFilter>('today');
  const listReq = useRef(0);
  const dayStart = board?.dayStart;

  useEffect(() => { const h = setTimeout(() => setDebounced(query.trim()), 250); return () => clearTimeout(h); }, [query]);
  useEffect(() => { setPage(1); }, [debounced, statusF, typeF, payF, range]);

  const loadList = useCallback(async () => {
    const id = ++listReq.current;
    setListLoading(true);
    setListError(false);
    const params: OrderListParams = {
      q: debounced || undefined,
      status: STATUS_FILTER[statusF],
      type: typeF === 'all' ? undefined : typeF,
      paymentStatus: payF === 'all' ? undefined : payF,
      ...rangeParams(range, dayStart),
      page,
      limit: LIST_LIMIT,
    };
    try {
      const r = await ordersService.list(params);
      if (id !== listReq.current) return;
      setRows(r.items);
      setTotal(r.total);
    } catch {
      if (id === listReq.current) setListError(true);
    } finally {
      if (id === listReq.current) setListLoading(false);
    }
  }, [debounced, statusF, typeF, payF, range, page, dayStart]);

  useEffect(() => { if (view === 'list') void loadList(); }, [view, loadList]);

  // ── shared ──
  const applyChange = (o: Order) => {
    setRows((arr) => arr.map((x) => (x.orderUid === o.orderUid ? o : x)));
    void loadBoard(true);
  };

  const advance = async (o: Order) => {
    const next = nextStatus(o);
    if (!next) return;
    setAdvancing(o.orderUid);
    // Optimistic: the card jumps columns straight away; the poll reconciles.
    setBoard((b) => b && moveOnBoard(b, o, next));
    try {
      const updated = await ordersService.setStatus(o.orderUid, next);
      toast.success(`#${o.number} → ${COLUMNS.find((c) => c.key === next)?.label.replace(' today', '') ?? next}`, { id: `adv-${o.orderUid}` });
      applyChange(updated);
    } catch {
      void loadBoard(true);
    } finally {
      setAdvancing(null);
    }
  };

  const columns = useMemo(() => {
    if (!board) return [];
    const byStatus = (s: OrderStatus) => board.active.filter((o) => o.status === s);
    return COLUMNS
      .filter((c) => c.key !== 'out_for_delivery' || board.hasDelivery)
      .map((c) => ({ ...c, orders: c.key === 'completed' ? board.completedToday : byStatus(c.key) }));
  }, [board]);

  const currency = board?.currency ?? companyCurrency;
  const openCount = board ? board.active.length : undefined;
  const nothingYet = board && board.active.length === 0 && board.completedToday.length === 0 && board.today.orders === 0 && board.counts.cancelled_today === 0;

  return (
    <>
      <Topbar
        title={plural}
        breadcrumb={`${t('operations')} / ${plural}`}
        description={board ? `${board.today.orders} today · ${formatMoney(board.today.revenue, currency)} taken in` : 'Live kitchen board'}
        right={
          <div className="flex items-center gap-2">
            <div className="flex rounded-lg border border-border bg-surface-alt p-0.5" role="radiogroup" aria-label="View">
              {(['board', 'list'] as View[]).map((v) => (
                <button key={v} type="button" role="radio" aria-checked={view === v} onClick={() => setView(v)}
                  className={clsx('inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold transition-colors', view === v ? 'bg-surface text-text-primary shadow-soft' : 'text-text-muted hover:text-text-primary')}>
                  {v === 'board' ? <Columns3 size={14} /> : <List size={14} />}
                  <span className="hidden sm:inline">{v === 'board' ? 'Board' : 'List'}</span>
                </button>
              ))}
            </div>
            {isAdmin && (
              <Button variant="secondary" iconLeft={<MessageCircle size={15} />} onClick={() => setNotifyOpen(true)} className="whitespace-nowrap" title="WhatsApp order updates">
                <span className="hidden lg:inline">WhatsApp updates</span>
              </Button>
            )}
            <Button iconLeft={<Plus size={15} />} onClick={() => navigate('/orders/new')} className="whitespace-nowrap">New {noun.toLowerCase()}</Button>
          </div>
        }
      />
      <OrderNotifyModal open={notifyOpen} onClose={closeNotify} />

      <div className="w-full px-4 py-6 sm:px-7 sm:py-7 space-y-5">
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <Kpi label="Orders today" value={board ? String(board.today.orders) : undefined} icon={<Receipt size={16} />} tone="bg-primary-soft text-primary"
            hint={board && board.counts.cancelled_today > 0 ? `${board.counts.cancelled_today} cancelled` : undefined} />
          <Kpi label="Taken in today" value={board ? formatMoney(board.today.revenue, currency) : undefined} icon={<Wallet size={16} />} tone="bg-success-soft text-success"
            hint={board && board.today.orders ? `Avg ${formatMoney(board.today.averageTicket, currency)}` : undefined} />
          <Kpi label="Open now" value={openCount !== undefined ? String(openCount) : undefined} icon={<ChefHat size={16} />} tone="bg-warning-soft text-warning"
            hint={board ? `${board.counts.new} new · ${board.counts.preparing} preparing` : undefined} />
          <Kpi label="Unpaid" value={board ? String(board.today.unpaid) : undefined} icon={<TriangleAlert size={16} />} tone="bg-danger-soft text-danger"
            hint={board && board.today.unpaid ? formatMoney(board.today.unpaidAmount, currency) : 'All settled'} />
        </div>

        {view === 'board' ? (
          boardError && !board ? (
            <ErrorBox what={`the ${plural.toLowerCase()} board`} onRetry={() => void loadBoard()} />
          ) : !board ? (
            <div className="-mx-4 overflow-x-auto px-4 pb-2 sm:mx-0 sm:px-0">
              <div className="flex gap-3">{COLUMNS.slice(0, 4).map((c) => <div key={c.key} className="skeleton h-[420px] w-[280px] shrink-0 rounded-2xl" />)}</div>
            </div>
          ) : nothingYet ? (
            <EmptyState
              icon={<ChefHat size={28} />}
              title={`No ${plural.toLowerCase()} yet today`}
              description={`Take your first ${noun.toLowerCase()} — pick items from your ${t('catalog').toLowerCase()}, choose dine-in, takeaway or delivery, and it lands on this board for the kitchen.`}
              action={<Button iconLeft={<Plus size={15} />} onClick={() => navigate('/orders/new')}>Take an {noun.toLowerCase()}</Button>}
            />
          ) : (
            <div className="-mx-4 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-0">
              <div className="flex min-h-[460px] gap-3">
                {columns.map((col) => (
                  <section key={col.key} className="flex w-[284px] shrink-0 flex-col rounded-2xl border border-border bg-surface-alt/40 xl:w-auto xl:min-w-[250px] xl:flex-1">
                    <header className="flex items-center justify-between gap-2 border-b border-border px-3.5 py-3">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className={clsx('h-2.5 w-2.5 shrink-0 rounded-full', col.key === 'completed' ? 'bg-text-muted' : STATUS_TONE[col.key].dot)} />
                        <span className="truncate text-sm font-semibold text-text-primary">{col.label}</span>
                        <span className="rounded-full bg-surface px-1.5 py-0.5 text-2xs font-semibold tabular-nums text-text-muted">
                          {col.key === 'completed' ? board.counts.completed_today : col.orders.length}
                        </span>
                      </div>
                      {col.key === 'out_for_delivery' && <Bike size={14} className="text-text-muted" />}
                    </header>
                    <div className="flex-1 space-y-2 overflow-y-auto p-2.5">
                      {col.orders.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-border/70 px-3 py-8 text-center text-2xs text-text-muted">{col.hint}</div>
                      ) : col.orders.map((o) => (
                        <OrderCard key={o.orderUid} order={o} now={now} busy={advancing === o.orderUid}
                          onOpen={() => navigate(`/orders/${o.orderUid}`)} onAdvance={() => void advance(o)} />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          )
        ) : (
          <div className="rounded-2xl border border-border bg-surface shadow-soft">
            <div className="flex flex-col gap-3 border-b border-border p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <div className="w-full lg:max-w-xs">
                  <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search #number, name or phone" iconLeft={<Search size={15} />}
                    iconRight={query ? <button type="button" aria-label="Clear search" onClick={() => setQuery('')} className="hover:text-text-primary"><X size={14} /></button> : undefined} />
                </div>
                <div className="flex flex-wrap gap-2 lg:ml-auto">
                  {(Object.keys(RANGE_LABEL) as RangeFilter[]).map((r) => (
                    <Chip key={r} active={range === r} onClick={() => setRange(r)}>{RANGE_LABEL[r]}</Chip>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {(['all', 'open', 'completed', 'cancelled'] as StatusFilter[]).map((s) => (
                  <Chip key={s} active={statusF === s} onClick={() => setStatusF(s)}>{s === 'all' ? 'Any status' : s[0].toUpperCase() + s.slice(1)}</Chip>
                ))}
                <span className="mx-1 hidden h-5 w-px bg-border sm:block" />
                {(['all', 'dine_in', 'takeaway', 'delivery'] as Array<OrderType | 'all'>).map((ty) => {
                  const Icon = ty === 'all' ? null : TYPE_ICON[ty];
                  return <Chip key={ty} active={typeF === ty} onClick={() => setTypeF(ty)}>{Icon && <Icon size={13} />}{ty === 'all' ? 'Any type' : ORDER_TYPE_LABEL[ty]}</Chip>;
                })}
                <span className="mx-1 hidden h-5 w-px bg-border sm:block" />
                <Chip active={payF === 'unpaid'} onClick={() => setPayF(payF === 'unpaid' ? 'all' : 'unpaid')}>Unpaid</Chip>
                <Chip active={payF === 'paid'} onClick={() => setPayF(payF === 'paid' ? 'all' : 'paid')}>Paid</Chip>
              </div>
            </div>

            {listError ? (
              <ErrorBox what={plural.toLowerCase()} onRetry={() => void loadList()} />
            ) : listLoading && rows.length === 0 ? (
              <div className="space-y-2 p-4">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
            ) : rows.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-14 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-alt text-text-muted"><Receipt size={20} /></span>
                <p className="text-sm font-semibold text-text-primary">No {plural.toLowerCase()} match</p>
                <p className="text-xs text-text-muted">Try another date range or clear the filters.</p>
              </div>
            ) : (
              <div className={clsx('transition-opacity', listLoading && 'opacity-60')}>
                <div className="hidden grid-cols-[84px_92px_minmax(0,1.3fr)_minmax(0,1.6fr)_110px_96px_120px] gap-3 border-b border-border bg-surface-alt px-4 py-2 text-2xs font-semibold uppercase tracking-[0.1em] text-text-muted lg:grid">
                  <span>#</span><span>Placed</span><span>{t('contacts', 'singular')} / table</span><span>Items</span><span className="text-right">Total</span><span>Payment</span><span>Status</span>
                </div>
                <ul className="divide-y divide-border">
                  {rows.map((o) => (
                    <li key={o.orderUid}>
                      <button type="button" onClick={() => navigate(`/orders/${o.orderUid}`)}
                        className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 px-4 py-3 text-left transition-colors hover:bg-surface-alt/60 lg:grid-cols-[84px_92px_minmax(0,1.3fr)_minmax(0,1.6fr)_110px_96px_120px] lg:items-center">
                        <span className="flex items-center gap-2 font-display text-sm font-bold text-text-primary">#{o.number}<span className="lg:hidden"><TypeBadge order={o} compact /></span></span>
                        <span className="text-right text-sm font-bold tabular-nums text-text-primary lg:hidden">{formatMoney(o.total, o.currency)}</span>
                        <span className="hidden text-xs tabular-nums text-text-muted lg:block" title={dateTimeOf(o.placedAt)}>{range === 'today' ? timeOf(o.placedAt) : dateTimeOf(o.placedAt)}</span>
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="hidden lg:inline-flex"><TypeBadge order={o} /></span>
                          <span className="truncate text-[13px] text-text-secondary">{o.customerName || (o.type === 'delivery' ? o.deliveryAddress : '') || <span className="text-text-muted">Walk-in</span>}</span>
                        </span>
                        <span className="col-span-2 truncate text-xs text-text-muted lg:col-span-1 lg:text-[13px]">{itemsSummary(o)}</span>
                        <span className="hidden text-right text-sm font-semibold tabular-nums text-text-primary lg:block">{formatMoney(o.total, o.currency)}</span>
                        <span className="flex items-center gap-2 lg:block"><PaymentBadge order={o} /><span className="lg:hidden"><StatusBadge status={o.status} /></span></span>
                        <span className="hidden lg:block"><StatusBadge status={o.status} /></span>
                      </button>
                    </li>
                  ))}
                </ul>
                {total > LIST_LIMIT && <div className="border-t border-border px-4"><Pagination page={page} total={total} limit={LIST_LIMIT} onChange={setPage} /></div>}
              </div>
            )}
          </div>
        )}
      </div>

      <OrderDrawer orderUid={orderUid ?? null} onClose={() => navigate('/orders')} onChanged={applyChange} />
    </>
  );
}

function moveOnBoard(b: OrderBoard, o: Order, next: Exclude<OrderStatus, 'cancelled'>): OrderBoard {
  const moved = { ...o, status: next, completedAt: next === 'completed' ? new Date().toISOString() : o.completedAt };
  const active = b.active.filter((x) => x.orderUid !== o.orderUid);
  if (next === 'completed') {
    return { ...b, active, completedToday: [moved, ...b.completedToday], counts: { ...b.counts, completed_today: b.counts.completed_today + 1 } };
  }
  return { ...b, active: [...active, moved].sort((x, y) => x.placedAt.localeCompare(y.placedAt)) };
}

function OrderCard({ order, now, busy, onOpen, onAdvance }: { order: Order; now: number; busy: boolean; onOpen: () => void; onAdvance: () => void }) {
  const next = nextStatus(order);
  const who = order.customerName || (order.type === 'delivery' ? order.deliveryAddress : null);
  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
      className="cursor-pointer rounded-xl border border-border bg-surface p-3 shadow-soft transition-all hover:-translate-y-px hover:shadow-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="font-display text-[15px] font-bold tracking-tight text-text-primary">#{order.number}</span>
          <TypeBadge order={order} />
        </div>
        <ElapsedPill order={order} now={now} />
      </div>
      {who && <div className="mt-1.5 truncate text-[12.5px] font-medium text-text-secondary">{who}</div>}
      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-text-muted">{itemsSummary(order)}</p>
      {order.items.some((l) => l.notes) && (
        <p className="mt-1 truncate text-2xs italic text-warning">{order.items.filter((l) => l.notes).map((l) => `${l.name}: ${l.notes}`).join(' · ')}</p>
      )}
      <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-border pt-2.5">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="text-[13px] font-bold tabular-nums text-text-primary">{formatMoney(order.total, order.currency)}</span>
          <PaymentBadge order={order} />
        </div>
        <span className="text-2xs tabular-nums text-text-muted">{order.itemCount} item{order.itemCount === 1 ? '' : 's'}</span>
      </div>
      {next && (
        <button
          type="button"
          disabled={busy}
          onClick={(e) => { e.stopPropagation(); onAdvance(); }}
          className={clsx(
            'mt-2.5 flex h-9 w-full items-center justify-center gap-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-60',
            next === 'completed' ? 'bg-success-soft text-success hover:bg-success hover:text-white' : 'bg-primary-soft text-primary hover:bg-primary hover:text-on-primary',
          )}
        >
          {busy ? <RefreshCw size={13} className="animate-spin" /> : next === 'completed' ? <CircleCheck size={14} /> : <ArrowRight size={14} />}
          {NEXT_LABEL[next as keyof typeof NEXT_LABEL]}
        </button>
      )}
    </article>
  );
}

function Kpi({ label, value, icon, tone, hint }: { label: string; value?: string; icon: React.ReactNode; tone: string; hint?: string }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-4 shadow-soft">
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-2xs font-semibold uppercase tracking-[0.12em] text-text-muted">{label}</span>
        <span className={clsx('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', tone)}>{icon}</span>
      </div>
      {value === undefined ? <div className="skeleton mt-2 h-7 w-16 rounded" /> : (
        <div className="mt-1 truncate font-display text-[22px] font-bold leading-tight tracking-tight tabular-nums text-text-primary sm:text-[26px]" title={typeof value === 'string' ? value : undefined}>{value}</div>
      )}
      <div className="mt-0.5 h-4 truncate text-2xs text-text-muted">{hint ?? ''}</div>
    </div>
  );
}

function ErrorBox({ what, onRetry }: { what: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-border bg-surface py-14 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-danger-soft text-danger"><TriangleAlert size={22} /></span>
      <div>
        <p className="text-sm font-semibold text-text-primary">Couldn’t load {what}</p>
        <p className="mt-1 text-xs text-text-muted">Check your connection and try again.</p>
      </div>
      <Button variant="secondary" size="sm" iconLeft={<RefreshCw size={14} />} onClick={onRetry}>Retry</Button>
    </div>
  );
}
